import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  SquaresFour,
  Folder,
  Flag,
  Lifebuoy,
  ClockCounterClockwise,
  Columns,
  CaretRight,
  CaretDown,
  Sparkle,
} from "@phosphor-icons/react";
import { fetchMyProjects } from "@/lib/api";
import { CollapseDoubleArrow, AIPlannerIcon } from "@/components/BrandIcons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Project-scoped left sidebar (iter-25 spec). Default expanded; collapse toggle
// at the bottom. Hosts global links (Dashboard / Projects) and project-scoped
// actions that used to live in the toolbar More menu (Milestones / Support /
// History / Manage columns).
export default function ProjectSidebar({
  collapsed,
  onToggleCollapsed,
  projectId,
  onOpenColumns,
  onOpenVersions,
  onOpenHeatmap,
  onOpenSupport,
}) {
  // Expand/collapse the "Projects" nav item to reveal the user's project list.
  // Persisted in localStorage so the user's choice survives page reloads.
  const [projectsOpen, setProjectsOpen] = useState(
    () => localStorage.getItem("tg_sidebar_projects_open") === "1",
  );
  const [projectList, setProjectList] = useState([]);
  // AI Planner is on the roadmap — clicking it opens a Coming Soon modal.
  const [aiComingSoon, setAiComingSoon] = useState(false);
  const location = useLocation();
  useEffect(() => {
    localStorage.setItem("tg_sidebar_projects_open", projectsOpen ? "1" : "0");
  }, [projectsOpen]);
  // Lazy-load the project list the first time the user expands the section.
  useEffect(() => {
    if (!projectsOpen || projectList.length) return;
    let cancel = false;
    (async () => {
      try {
        const d = await fetchMyProjects();
        if (!cancel) setProjectList(d?.projects || []);
      } catch (_) {
        if (!cancel) setProjectList([]);
      }
    })();
    return () => { cancel = true; };
  }, [projectsOpen, projectList.length]);

  const items = [
    { id: "dashboard", label: "Dashboard", icon: SquaresFour, href: "/dashboard" },
    { id: "milestones", label: "Milestones", icon: Flag, onClick: onOpenHeatmap },
    { id: "history", label: "History", icon: ClockCounterClockwise, onClick: onOpenVersions },
    { id: "columns", label: "Manage columns", icon: Columns, onClick: onOpenColumns },
    {
      id: "ai-planner",
      label: "AI Planner",
      icon: AIPlannerIcon,
      onClick: () => setAiComingSoon(true),
      isCustomIcon: true,
      badge: "NEW",
    },
    { id: "support", label: "Support", icon: Lifebuoy, onClick: onOpenSupport },
  ];

  return (
    <aside
      className={`shrink-0 border-r border-slate-200 bg-white flex flex-col transition-[width] duration-150 ${
        collapsed ? "w-14" : "w-56"
      }`}
      data-testid="project-sidebar"
    >
      <div className="flex-1 overflow-y-auto py-3 space-y-1 px-2">
        {/* Dashboard (first item, always present) */}
        <Link
          to="/dashboard"
          className="group flex items-center gap-3 h-10 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 transition-colors"
          title={collapsed ? "Dashboard" : undefined}
          data-testid="sidebar-dashboard"
        >
          <span className="h-10 w-10 flex items-center justify-center shrink-0">
            <SquaresFour size={18} weight="bold" />
          </span>
          {!collapsed && (
            <span className="text-sm font-medium tracking-tight truncate">Dashboard</span>
          )}
        </Link>

        {/* Projects — expandable dropdown of the user's projects (replaces the
            previous duplicate link to /dashboard). When collapsed, falls back
            to a simple link to /dashboard so the icon still does something. */}
        {collapsed ? (
          <Link
            to="/dashboard"
            className="group flex items-center gap-3 h-10 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 transition-colors"
            title="Projects"
            data-testid="sidebar-projects"
          >
            <span className="h-10 w-10 flex items-center justify-center shrink-0">
              <Folder size={18} weight="bold" />
            </span>
          </Link>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setProjectsOpen((v) => !v)}
              className="group w-full flex items-center gap-3 h-10 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 transition-colors text-left"
              data-testid="sidebar-projects"
              aria-expanded={projectsOpen}
            >
              <span className="h-10 w-10 flex items-center justify-center shrink-0">
                <Folder size={18} weight="bold" />
              </span>
              <span className="flex-1 text-sm font-medium tracking-tight truncate">Projects</span>
              <span className="mr-2 text-slate-400">
                {projectsOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              </span>
            </button>
            {projectsOpen && (
              <ul className="ml-10 mt-1 mb-2 space-y-0.5" data-testid="sidebar-projects-list">
                {projectList.length === 0 ? (
                  <li className="text-[11px] text-slate-400 px-2 py-1">Loading…</li>
                ) : (
                  projectList.map((p) => {
                    const active = location.pathname === `/project/${p.id}` || projectId === p.id;
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/project/${p.id}`}
                          className={`block px-2 py-1.5 rounded-md text-xs truncate transition-colors ${
                            active
                              ? "bg-slate-950 text-white"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                          }`}
                          title={p.name}
                          data-testid={`sidebar-project-${p.id}`}
                        >
                          {p.name}
                        </Link>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>
        )}

        {items.slice(1).map((it) => {
          const Icon = it.icon;
          const isHighlighted = it.id === "ai-planner";
          const base = isHighlighted
            ? "group flex items-center gap-3 h-10 rounded-md text-slate-700 hover:text-slate-950 transition-colors ai-planner-button"
            : "group flex items-center gap-3 h-10 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 transition-colors";
          // Phosphor icons accept `weight`, our custom SVG icons don't —
          // branch the render so we don't pass invalid props.
          const iconEl = it.isCustomIcon
            ? <Icon size={18} />
            : <Icon size={18} weight="bold" />;
          const inner = (
            <>
              <span className="h-10 w-10 flex items-center justify-center shrink-0">
                {iconEl}
              </span>
              {!collapsed && (
                <>
                  <span className="text-sm font-medium tracking-tight truncate flex-1">{it.label}</span>
                  {it.badge && (
                    <span className="mr-2 text-[9px] font-mono-ibm uppercase tracking-widest px-1.5 py-0.5 rounded-md tg-gradient text-white">
                      {it.badge}
                    </span>
                  )}
                </>
              )}
            </>
          );
          if (it.href) {
            return (
              <Link
                key={it.id}
                to={it.href}
                className={base}
                title={collapsed ? it.label : undefined}
                data-testid={`sidebar-${it.id}`}
              >
                {inner}
              </Link>
            );
          }
          return (
            <button
              key={it.id}
              type="button"
              onClick={it.onClick}
              className={`${base} w-full text-left`}
              title={collapsed ? it.label : undefined}
              data-testid={`sidebar-${it.id}`}
            >
              {inner}
            </button>
          );
        })}
      </div>

      {/* Collapse toggle — left-aligned (matches other sidebar items), custom
          double-arrow icon. Rotates 180° when sidebar is already collapsed so
          the same icon represents "expand". */}
      <div className="border-t border-slate-200 p-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="group w-full flex items-center gap-3 h-10 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-950 transition-colors text-left"
          data-testid="sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span className="h-10 w-10 flex items-center justify-center shrink-0">
            <CollapseDoubleArrow
              size={18}
              className={collapsed ? "rotate-180 transition-transform" : "transition-transform"}
            />
          </span>
          {!collapsed && (
            <span className="text-sm font-medium tracking-tight truncate">Collapse</span>
          )}
        </button>
      </div>

      {/* AI Planner — coming soon modal */}
      <Dialog open={aiComingSoon} onOpenChange={setAiComingSoon}>
        <DialogContent className="rounded-md max-w-md" data-testid="ai-coming-soon-dialog">
          <DialogHeader>
            <div className="h-12 w-12 rounded-md tg-gradient flex items-center justify-center mb-3 text-white">
              <AIPlannerIcon size={26} color="#fff" />
            </div>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              AI Planner — coming soon
            </DialogTitle>
            <DialogDescription className="text-slate-600 leading-relaxed">
              Describe your project in plain English and AI Planner will draft a starting timeline, suggest dependencies, and flag risks. We&apos;re putting the finishing touches on it now — you&apos;ll see it land in a future release.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setAiComingSoon(false)}
              className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="ai-coming-soon-close"
            >
              <Sparkle size={14} weight="fill" className="mr-2" />
              Sounds good
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
